import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Mail, Phone as PhoneIcon, MapPin } from 'lucide-react';
import { useToast } from './ToastContainer';
import api from '../utils/api';
import './Shopify.css';

const moneyFmt = (v, code) => {
  if (v == null) return '—';
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency', currency: code || 'USD', maximumFractionDigits: 0,
    }).format(Number(v));
  } catch { return `${code || ''} ${Number(v).toLocaleString()}`; }
};
const dateFmt = (iso) => iso ? new Date(iso).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';

const ShopifyCustomerDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { showError } = useToast();
  const [customer, setCustomer] = useState(null);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/api/shopify/customers/${id}`)
      .then(r => { setCustomer(r.data.customer); setOrders(r.data.orders || []); })
      .catch(e => showError('Failed to load: ' + (e.response?.data?.error || e.message)))
      .finally(() => setLoading(false));
  }, [id, showError]);

  if (loading) return (<div className="shop-loading"><div className="shop-spinner" /></div>);
  if (!customer) return (<div className="shop"><div className="shop-empty">Customer not found.</div></div>);

  return (
    <div className="shop">
      <button onClick={() => navigate(-1)} className="shop-action-btn" style={{ marginBottom: '1rem' }}>
        <ArrowLeft size={14} /> Back
      </button>

      <div className="shop-header">
        <div>
          <h1 className="shop-title">{customer.display_name || customer.email}</h1>
          <p className="shop-subtitle">
            Customer since {dateFmt(customer.shopify_created_at)} · {customer.state}
          </p>
        </div>
        {customer.vip_tier && (
          <span className="shop-badge" style={{ background: tierBg(customer.vip_tier), color: tierColor(customer.vip_tier), fontSize: '0.85rem', padding: '0.4rem 0.8rem' }}>
            {customer.vip_tier} VIP
          </span>
        )}
      </div>

      <div className="shop-kpis">
        <KpiBox label="Lifetime spend" value={moneyFmt(customer.total_spent_amount, customer.total_spent_currency)} accent="green" />
        <KpiBox label="Total orders" value={String(customer.total_orders)} accent="blue" />
        <KpiBox label="Avg order value" value={moneyFmt(customer.total_orders > 0 ? customer.total_spent_amount / customer.total_orders : 0, customer.total_spent_currency)} accent="amber" />
        <KpiBox label="Tags" value={customer.tags?.length ? customer.tags.length : 0} accent="neutral" sub={customer.tags?.slice(0, 3).join(', ')} />
      </div>

      <div className="shop-panel">
        <div className="shop-panel-head"><h3>Contact & address</h3></div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem', fontSize: '0.85rem' }}>
          <div>
            <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.72rem', textTransform: 'uppercase' }}><Mail size={12} style={{ verticalAlign: 'middle' }} /> Email</div>
            <div style={{ marginTop: '0.25rem' }}>{customer.email || '—'} {customer.verified_email ? '✓' : ''}</div>
          </div>
          <div>
            <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.72rem', textTransform: 'uppercase' }}><PhoneIcon size={12} style={{ verticalAlign: 'middle' }} /> Phone</div>
            <div style={{ marginTop: '0.25rem' }}>{customer.phone || '—'}</div>
          </div>
          <div>
            <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.72rem', textTransform: 'uppercase' }}><MapPin size={12} style={{ verticalAlign: 'middle' }} /> Default city</div>
            <div style={{ marginTop: '0.25rem' }}>{customer.default_city || '—'}, {customer.default_country || '—'}</div>
          </div>
        </div>
      </div>

      <div className="shop-panel">
        <div className="shop-panel-head">
          <h3>Order history</h3>
          <span className="shop-panel-meta">{orders.length} most recent</span>
        </div>
        {orders.length === 0 ? (
          <div className="shop-empty">No orders yet for this customer.</div>
        ) : (
          <table className="shop-table">
            <thead>
              <tr><th>Order</th><th>Total</th><th>Refunded</th><th>Status</th><th>City</th><th>Date</th></tr>
            </thead>
            <tbody>
              {orders.map(o => (
                <tr key={o.id}>
                  <td><Link to={`/shopify/orders/${o.id}`} className="shop-order-link">{o.name}</Link></td>
                  <td>{moneyFmt(o.total_price, o.currency_code)}</td>
                  <td style={{ color: o.total_refunded > 0 ? '#f87171' : 'rgba(255,255,255,0.4)' }}>
                    {o.total_refunded > 0 ? moneyFmt(o.total_refunded, o.currency_code) : '—'}
                  </td>
                  <td><span className={`shop-badge ${(o.display_financial_status || '').toLowerCase()}`}>{o.display_financial_status}</span></td>
                  <td>{o.shipping_city || '—'}</td>
                  <td className="shop-cell-time">{dateFmt(o.shopify_created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

const KpiBox = ({ label, value, accent, sub }) => (
  <div className="shop-kpi">
    <div className="shop-kpi-head"><span className={`shop-kpi-icon ${accent}`} /> {label}</div>
    <div className={`shop-kpi-value ${accent}`}>{value}</div>
    {sub && <div className="shop-kpi-sub">{sub}</div>}
  </div>
);

const tierColor = (t) => ({ Gold: '#fbbf24', Silver: '#e5e7eb', Bronze: '#fb923c' }[t] || '#fff');
const tierBg = (t) => ({
  Gold: 'rgba(251, 191, 36, 0.18)',
  Silver: 'rgba(229, 231, 235, 0.18)',
  Bronze: 'rgba(251, 146, 60, 0.18)',
}[t] || 'rgba(255,255,255,0.06)');

export default ShopifyCustomerDetail;
